/**
 * Platform-neutral routing contract (ELARA-704).
 *
 * This module is the single source of truth for the **canonical route vocabulary** and the
 * **navigation contract** shared by the web client and the React Native shell (`@elara/mobile`).
 * It is deliberately **DOM-free** — it imports only other DOM-free modules and references no `window`/`document`/`react`
 * types — so it compiles under the mobile no-DOM tsconfig and passes the `@elara/mobile` eslint
 * boundary. The web modules that historically owned these declarations
 * (`RouterState.ts`, `constants.ts`, `routes.ts`, `navigation-port.ts`) now re-export from here so
 * web call sites are unchanged.
 *
 * Each platform still owns its own *screen mapping*: the web router renders eager page components for
 * these paths, while mobile maps the same paths onto React Navigation screens. Only the path/param
 * vocabulary and the {@link NavigationPort} interface are shared.
 */

import type { PreviewLinkTarget } from '@elara/domain/schemas/preview-schemas';

import type { GuideOpenMode, GuideOpenOptions, GuideStack, GuideTarget } from './guide-stack.js';

/** A parsed route: the pathname, its extracted params, query params, and optional hash fragment. */
export interface Route {
  path: string;
  params: Record<string, string>;
  query: Record<string, string>;
  hash?: string;
}

/**
 * Route guard for controlling navigation.
 *
 * Guards must be synchronous since browser navigation cannot be async. For async checks (like server
 * validation), implement a different pattern (e.g., navigation intent + confirmation flow).
 */
export interface RouteGuard {
  /** Called before navigation. Return false to block navigation. Must be synchronous. */
  canLeave: (currentRoute: Route, targetRoute: Route) => boolean;
  /** Optional callback when leaving the route (after `canLeave` returns true). Use for cleanup. */
  onLeave?: (currentRoute: Route, targetRoute: Route) => void;
}

/**
 * Platform-neutral navigation contract (ELARA-694 / ELARA-704).
 *
 * The signal stores and hooks that drive navigation must not reach for `window.history`,
 * `document.title`, or `window.confirm` directly — those globals don't exist on React Native and
 * crash the bundle. `NavigationPort` is the seam they depend on instead: the web router
 * (`RouterState`) implements it on web, and the React Navigation shell implements it on native
 * (`createMobileNavigationPort`).
 *
 * Method names mirror the existing `RouterState` surface to keep this web-neutral (no call-site
 * churn); the web-coupled concepts map as: `setTitle` → {@link setPageTitle}, `updateQuery` →
 * {@link updateQueryParams}, `preview` → {@link updateHashFragment}/{@link removeHashParams}.
 */
export interface NavigationPort {
  /** The current route (signal-backed; reactive when read inside a tracked scope). */
  readonly currentRoute: Route | undefined;

  /**
   * The entity-preview sheet currently open, if any (signal-backed; reactive). A lot of content —
   * destination/hotel/place/flight guides — is presented in sheets that overlay the route rather than
   * changing the pathname, so this is the cross-platform authority for "which sheet is foreground".
   * On web it is derived from the route's `#preview=<type>&id=<id>` hash; on native it is a
   * signal-first value (the hash is a web-only serialization).
   */
  readonly previewTarget: PreviewLinkTarget | undefined;

  /**
   * The guides walked through to reach the open one (signal-backed; reactive), or `undefined` when no
   * guide is open. Its current entry is always {@link previewTarget}. Kept by the platform because it
   * is a slice of platform history: browser history on web (so browser Back/Forward move through it),
   * the guide's own navigation stack on native.
   */
  readonly guideStack: GuideStack | undefined;

  /**
   * Open a guide. `reset` starts a new stack (opened from outside any guide), optionally with a
   * `parent` beneath it; `push` follows a link inside a guide, `replace` swaps the current guide (a
   * redirect). See `guide-stack.ts`.
   */
  openGuide(target: GuideTarget, mode: GuideOpenMode, options?: GuideOpenOptions): void;

  /** Move to another entry of the open stack — Back, Forward or a breadcrumb jump. */
  goToGuide(index: number): void;

  /** Close the guide, ending its stack. */
  closeGuide(): void;

  /** Record the open guide's title once it has rendered, for the breadcrumb. */
  setGuideTitle(title: string): void;

  /** Navigate to a path, optionally replacing the current history entry. */
  navigate(path: string, options?: { replace?: boolean; state?: unknown }): void;

  /** Navigate back to the previous entry. */
  goBack(): void;

  /**
   * Whether back navigation is possible. A method (not a getter) so it reflects live history — web
   * reads `window.history` length; native maps to React Navigation's `canGoBack()`.
   */
  canGoBack(): boolean;

  /** Set the page/screen title. Abstracts `document.title` on web. */
  setPageTitle(title: string): void;

  /**
   * Ask the user to confirm leaving the current screen (e.g. unsaved changes). Returns `true` to
   * proceed with navigation. Abstracts `window.confirm` on web.
   */
  confirmLeave(message: string): boolean;

  /** Merge query params onto the current route. */
  updateQueryParams(params: Record<string, string>): void;

  /** Remove the named query params from the current route. */
  removeQueryParams(names: string[]): void;

  /** Merge preview/hash params onto the current route. */
  updateHashFragment(params: Record<string, string>): void;

  /** Remove the named preview/hash params from the current route. */
  removeHashParams(names: string[]): void;

  /** Pure route-pattern match helper (e.g. `/trip/123` against `/trip/:id`). */
  doesPathMatch(path: string, pattern: string): boolean;

  /** Register a guard consulted before navigation leaves a route. */
  addGuard(guard: RouteGuard): void;

  /** Remove a previously registered guard. */
  removeGuard(guard: RouteGuard): void;
}

/**
 * Canonical route paths, shared by web and mobile.
 *
 * `ADMIN` is web-only (mobile drops it); the trip panel route (`TRIP_PANEL`) carries the active
 * panel as a path segment so the URL is the same on both platforms (ELARA-704).
 */
export const ROUTES = {
  HOME: '/',
  TRIP: '/trip/:id',
  /** Trip with an explicit panel as a path segment, e.g. `/trip/abc/shortlist`. */
  TRIP_PANEL: '/trip/:id/:panel',
  TRIP_NEW: '/trip/new',
  /** A featured Story article (web-only page; mobile opens a sheet instead). */
  STORY: '/story/:storyId',
  ACCOUNT: '/account',
  PROFILE: '/account/profile',
  FAMILY: '/account/family',
  TRAVEL_PREFERENCES: '/account/travel-preferences',
  NOTIFICATIONS: '/account/notifications',
  PAYMENT_METHODS: '/account/payment-methods',
  SETTINGS: '/account/settings',
  SECURITY: '/account/security',
  FIREBASE_ACTION: '/auth/action',
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ONBOARDING: '/onboarding',
  /**
   * Membership payment gate. A member whose account exists but has no subscription lands here —
   * whether they have just signed up or are signing back in on a new session — and cannot reach
   * the app until the first payment succeeds.
   */
  MEMBERSHIP_PAYMENT: '/membership/payment',
  /**
   * Phone verification gate (ELARA-1170). A member with no verified phone whose account is more than
   * a day old lands here, and cannot reach the app until they verify one. Web-only; mobile covers the
   * app with `PhoneRequiredGate` instead.
   */
  VERIFY_PHONE: '/verify-phone',
  /** Hotel checkout: items in `?items=id1,id2`. State lives per-itinerary-item; no separate booking doc. */
  CHECKOUT_HOTEL: '/checkout/hotel/:tripId',
  /** Single flight checkout URL keyed by flightOptionId — page branches on whether a FlightBooking exists for the option. */
  CHECKOUT_FLIGHT: '/checkout/flight/:tripId/:flightOptionId',
  ADMIN: '/admin/*',
  /**
   * Public read-only itinerary behind a trip share link (web-only). Open to signed-out visitors and
   * rendered outside every signed-in gate; mobile never routes here (recipients open it in a browser).
   */
  SHARED_ITINERARY: '/share/:token',
} as const;

/**
 * Route parameter types for type-safe navigation.
 *
 * Maps route paths to their expected parameter objects. Routes with no parameters use `never`.
 */
export interface RouteParams {
  '/': never;
  '/trip/:id': { id: string };
  '/trip/:id/:panel': { id: string; panel: string };
  '/trip/new': never;
  '/story/:storyId': { storyId: string };
  '/account': never;
  '/account/profile': never;
  '/account/family': never;
  '/account/travel-preferences': never;
  '/account/notifications': never;
  '/account/payment-methods': never;
  '/account/settings': never;
  '/account/security': never;
  '/onboarding': never;
  '/membership/payment': never;
  '/verify-phone': never;
  '/checkout/hotel/:tripId': { tripId: string };
  '/checkout/flight/:tripId/:flightOptionId': { tripId: string; flightOptionId: string };
  '/auth/action': never;
  '/login': never;
  '/signup': never;
  '/forgot-password': never;
  '/reset-password': never;
  '/admin/*': never;
  '/share/:token': { token: string };
}

/** Extract route path from the {@link ROUTES} constant. */
export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Helper type to get the parameters for a specific route. */
export type GetRouteParams<T extends RoutePath> = RouteParams[T];

/** The four panels of a trip. The active panel is carried in the URL path (ELARA-704). */
export type TripPanelType = 'chat' | 'shortlist' | 'itinerary' | 'bookings';

/** Ordered list of every valid trip panel, for iteration and validation. */
export const TRIP_PANELS: readonly TripPanelType[] = ['chat', 'shortlist', 'itinerary', 'bookings'];

/** Type guard narrowing an arbitrary string to a valid {@link TripPanelType}. */
export function isTripPanel(value: string): value is TripPanelType {
  return (TRIP_PANELS as readonly string[]).includes(value);
}

/**
 * Build the canonical path for a trip panel.
 *
 * @example
 * tripPanelPath('abc', 'shortlist'); // => '/trip/abc/shortlist'
 */
export function tripPanelPath(tripId: string, panel: TripPanelType): string {
  return `/trip/${encodeURIComponent(tripId)}/${panel}`;
}

/**
 * Whether a path is a trip route — both `/trip/:id` and the panel form `/trip/:id/:panel` (and the
 * `/trip/new` entry, like the legacy `/trip/:id` match did).
 *
 * Centralized so every store that reacts to "am I on a trip?" treats a panel path the same as the
 * bare trip path (ELARA-704). A site that checks only `/trip/:id` silently reads `/trip/:id/:panel`
 * as "left the trip" and tears down trip/chat state — which is exactly the bug this prevents.
 *
 * @example
 * isTripRoutePath('/trip/abc');           // => true
 * isTripRoutePath('/trip/abc/shortlist'); // => true
 * isTripRoutePath('/account');            // => false
 */
export function isTripRoutePath(path: string): boolean {
  const segments = path.split('/').filter(Boolean);
  return segments[0] === 'trip' && (segments.length === 2 || segments.length === 3);
}
